import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { X, MessageCircle, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

// In-app notification toast component
const NotificationToast = ({ notification, onDismiss, onClick }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(notification.id);
    }, 5000); // Auto dismiss after 5 seconds
    
    return () => clearTimeout(timer);
  }, [notification.id, onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 300, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 300, scale: 0.9 }}
      className="bg-card border border-white/10 rounded-lg shadow-xl p-3 max-w-xs cursor-pointer hover:bg-white/5 transition-colors"
      onClick={() => {
        onClick?.(notification);
        onDismiss(notification.id);
      }}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`p-2 rounded-full flex-shrink-0 ${
          notification.type === 'dm' ? 'bg-blue-500/20 text-blue-400' :
          notification.type === 'chat' ? 'bg-primary/20 text-primary' :
          'bg-gray-500/20 text-gray-400'
        }`}>
          {notification.type === 'dm' ? (
            <MessageCircle className="w-4 h-4" />
          ) : (
            <Bell className="w-4 h-4" />
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">
            {notification.title}
          </p>
          <p className="text-xs text-gray-400 truncate mt-0.5">
            {notification.message}
          </p>
        </div>
        
        {/* Close button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(notification.id);
          }}
          className="p-1 hover:bg-white/10 rounded text-gray-400 hover:text-white flex-shrink-0"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </motion.div>
  );
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [inAppEnabled, setInAppEnabled] = useState(true);

  // Load user's in-app notification preference
  useEffect(() => {
    if (user?.in_app_notifications !== undefined) {
      setInAppEnabled(user.in_app_notifications);
    }
  }, [user]);

  // Add a new notification
  const addNotification = useCallback((notification) => {
    if (!inAppEnabled) return;
    
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { ...notification, id }]);
  }, [inAppEnabled]);

  // Remove a notification
  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Show a DM notification
  const showDMNotification = useCallback((senderId, senderName, message, profilePic) => {
    if (!inAppEnabled || !user) return;
    
    addNotification({
      type: 'dm',
      title: senderName,
      message: message.length > 50 ? message.slice(0, 50) + '...' : message,
      data: { senderId, profilePic }
    });
  }, [inAppEnabled, user, addNotification]);

  // Show a chat mention notification
  const showChatNotification = useCallback((senderName, message) => {
    if (!inAppEnabled || !user) return;
    
    addNotification({
      type: 'chat',
      title: `${senderName} in Global Chat`,
      message: message.length > 50 ? message.slice(0, 50) + '...' : message,
      data: {}
    });
  }, [inAppEnabled, user, addNotification]);

  // Handle notification click
  const handleNotificationClick = useCallback((notification) => {
    if (notification.type === 'dm' && notification.data?.senderId) {
      navigate(`/dm/${notification.data.senderId}`);
    } else if (notification.type === 'chat') {
      navigate('/chat');
    }
  }, [navigate]);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      inAppEnabled,
      setInAppEnabled,
      addNotification,
      dismissNotification,
      showDMNotification,
      showChatNotification,
      clearAll
    }}>
      {children}
      
      {/* Notification container - fixed position top right */}
      <div className="fixed top-20 right-4 z-[200] space-y-2 pointer-events-none">
        <AnimatePresence>
          {notifications.slice(-3).map(notification => (
            <div key={notification.id} className="pointer-events-auto">
              <NotificationToast
                notification={notification}
                onDismiss={dismissNotification}
                onClick={handleNotificationClick}
              />
            </div>
          ))}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
